import React from 'react'
import { Delete } from '@classy/campaign-page-blocks'
import { type Frequency } from '@classy/campaign-page-blocks'
import * as Styled from './CartItem.styled'
import { useTranslation } from 'next-i18next'

interface CartItemProps {
  id: string
  title: string
  description?: string
  frequency?: Frequency
  amount: number
  currency: string
  onDelete: (id: string) => void
  endDate?: string
}

export const CartItem = ({
  id,
  title,
  description,
  frequency,
  amount,
  currency,
  onDelete,
  endDate,
}: CartItemProps) => {
  const { t } = useTranslation('cart')
  const [isFadingOut, setIsFadingOut] = React.useState(false)
  const cartItemCardRef = React.useRef<HTMLDivElement>(null)

  const handleRemoveItem = () => {
    setIsFadingOut(true)

    const cartItemCard = cartItemCardRef.current

    cartItemCard?.addEventListener(
      'transitionend',
      () => {
        onDelete(id)
      },
      { once: true },
    )
  }

  return (
    <Styled.Card
      data-id="cp-cart-item"
      ref={cartItemCardRef}
      className={isFadingOut ? 'fadeout' : ''}
      // 105px is the height of a cart item card where the header/description are only one line
      cartItemCardHeight={cartItemCardRef.current?.clientHeight || 105}
    >
      <Styled.InfoContainer>
        <Styled.Title>{title}</Styled.Title>
        <Styled.ButtonContainer
          data-id="cp-cart-item-delete"
          onClick={handleRemoveItem}
          aria-label={t('CartItem.removeCartItem', 'Remove donation')}
        >
          <Delete />
        </Styled.ButtonContainer>
      </Styled.InfoContainer>

      {description && <Styled.Description>{description}</Styled.Description>}
      <Styled.InfoContainer>
        <Styled.FrequencyBadge>
          <Styled.Frequency>
            {frequency
              ? t(`CartItem.frequency.${frequency}`, frequency)
              : t('CartItem.frequencyError', 'Error')}
            {endDate
              ? t('CartItem.until', ' until ') +
                new Intl.DateTimeFormat('en-US').format(new Date(`${endDate}T00:00:00`))
              : null}
          </Styled.Frequency>
        </Styled.FrequencyBadge>
        <Styled.Amount>
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
          }).format(amount)}
        </Styled.Amount>
      </Styled.InfoContainer>
    </Styled.Card>
  )
}
